import React from 'react';
import {
  MOBILE_PLAYER_DRONE_BOTTOM_PX,
  MOBILE_PLAYER_DRONE_GAP_PX,
  MOBILE_PLAYER_DRONE_RIGHT_PX,
  MOBILE_PLAYER_DRONE_SCALE,
} from './mobileBattleLayoutOffsets';
import AbilityTooltip from '../../utils/AbilityTooltip';
import { buildDeployedDroneTooltipText } from '../../utils/deployedDroneTooltipText';
import { getOrderCardImageSrc } from '../../utils/orderCardImageSrc';

/**
 * Mobile Player Drones Component
 * Mirrors desktop `.player-drone-zone`: right 20, ~58px above the end-turn anchor (bottom 63 on mobile).
 */
export const MobilePlayerDrones = ({
  you,
  droneRenderEntries = null,
  gameState,
  yourTurn,
  deployTargetMode,
  selectedDeployOrderId,
  handleTargetDeploy,
  replaceDroneTargetMode,
  selectedReplaceDroneInstanceId,
  setClickedDroneForReplace,
  clickedDroneForReplace,
  selectedOrderInstanceId,
  setReplaceDroneTargetMode,
  setSelectedReplaceDroneId,
  selectedOrderId,
  setSelectedReplaceDroneInstanceId,
  activateDrone,
  playDrone,
  handleReplaceDrone,
  handleCancelReplaceDrone
}) => {
  const entries = droneRenderEntries ?? (you?.drones || [])
    .map((drone, index) => (drone && !drone.markedForRemoval ? { drone, index, isGhost: false } : null))
    .filter(Boolean);

  return (
    <div
      className="player-drone-zone-mobile"
      data-player-drone-zone="true"
      style={{
        position: "absolute",
        bottom: `${MOBILE_PLAYER_DRONE_BOTTOM_PX}px`,
        right: `${MOBILE_PLAYER_DRONE_RIGHT_PX}px`,
        zIndex: 1300,
        display: "flex",
        gap: `${MOBILE_PLAYER_DRONE_GAP_PX}px`,
        flexDirection: "row",
        transform: `scale(${MOBILE_PLAYER_DRONE_SCALE})`,
        transformOrigin: "bottom right"
      }}
    >
      {entries.map(({ drone, index, isGhost }) => {
        const allPlayerIds = gameState?.players || (gameState?.state ? Object.keys(gameState.state) : []);
        let disruptorDeployed = false;
        for (const pid of allPlayerIds) {
          const p = gameState?.state?.[pid];
          if (p && p.playedOrders) {
            for (const zone of ['front', 'flank']) {
              if (p.playedOrders[zone]) {
                for (const unit of p.playedOrders[zone]) {
                  if (unit && unit.id === 502 && unit.health > 0) {
                    disruptorDeployed = true;
                    break;
                  }
                }
                if (disruptorDeployed) break;
              }
            }
            if (disruptorDeployed) break;
          }
        }
        
        const canActivate = !isGhost && yourTurn && (drone.charges > 0 || drone.charges === undefined) && !drone.usedThisTurn && !disruptorDeployed;
        const selectedHandOrder = selectedOrderInstanceId ? you?.hand?.find(o => o.instanceId === selectedOrderInstanceId) : null;
        const isHandDroneTargeting = yourTurn && selectedHandOrder?.type === 'Drone' && !replaceDroneTargetMode;
        
        return (
          <AbilityTooltip
            as="div"
            key={`player-drone-mobile-${drone.instanceId || index}`}
            className="drone-slot-bounds"
            tooltipText={buildDeployedDroneTooltipText(drone, { disruptorDeployed, playerOwned: true })}
            data-drone-zone="your_drone"
            data-drone-index={index}
            data-instance-id={drone.instanceId || ''}
            style={{
              width: '80px',
              height: '60px',
              backgroundColor: '#333',
              border: isHandDroneTargeting ? '2px solid #ffd400' : (canActivate ? '2px solid #2c5aa0' : '2px solid #444'),
              borderRadius: '4px',
              cursor: isGhost ? 'default' : (canActivate ? 'pointer' : 'default'),
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (canActivate || isHandDroneTargeting) ? 1 : 0.6,
              boxShadow: isHandDroneTargeting ? '0 0 10px rgba(255,212,0,0.9)' : 'none',
              overflow: 'visible',
              pointerEvents: isGhost ? 'none' : 'auto',
            }}
            onClick={() => {
              if (isGhost) {
                return;
              }
              if (deployTargetMode && selectedDeployOrderId) {
                handleTargetDeploy('drone', index, 'friendly');
                return;
              }
              
              if (replaceDroneTargetMode && selectedReplaceDroneInstanceId) {
                setClickedDroneForReplace({
                  ...drone,
                  index
                });
                return;
              }
              
              if (selectedOrderInstanceId && !replaceDroneTargetMode) {
                const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
                if (selectedOrder?.type === 'Drone') {
                  const dronesCount = you.drones?.filter(d => !d.markedForRemoval).length || 0;
                  if (dronesCount >= 4) {
                    setReplaceDroneTargetMode(true);
                    setSelectedReplaceDroneId(selectedOrderId);
                    setSelectedReplaceDroneInstanceId(selectedOrderInstanceId);
                    setClickedDroneForReplace({
                      ...drone,
                      index
                    });
                    return;
                  }
                  // Zone has free slots: clicking any drone slot deploys selected hand drone.
                  playDrone(selectedOrder.id);
                  return;
                }
              }
              
              if (canActivate) {
                activateDrone(drone.instanceId);
              }
            }}
          >
            <img
              src={getOrderCardImageSrc(drone)}
              alt={drone.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 1
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {drone.charges !== undefined && drone.charges > 0 && (
              <div style={{
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
                position: 'absolute',
                top: '2px',
                right: '2px',
                backgroundColor: 'rgba(0,0,0,0.7)',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2
              }}>
                {drone.charges}
              </div>
            )}
            
            {replaceDroneTargetMode && clickedDroneForReplace && clickedDroneForReplace.instanceId === drone.instanceId && (
              <div style={{
                position: 'absolute',
                bottom: '3px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '6px',
                zIndex: 3
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReplaceDrone(clickedDroneForReplace);
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    padding: 0
                  }}
                  data-tooltip="Replace drone"
                >
                  ✓
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelReplaceDrone();
                  }}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#f44336',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.5)',
                    padding: 0
                  }}
                  data-tooltip="Cancel replacement"
                >
                  ✗
                </button>
              </div>
            )}
          </AbilityTooltip>
        );
      })}
      {/* Empty drone slots */}
      {Array.from({ length: 4 - entries.length }).map((_, index) => (
        <div
          key={`empty-player-drone-mobile-${index}`}
          data-drone-index={index + entries.length}
          data-drone-zone="your_drone"
          onClick={() => {
            if (!yourTurn) return;
            if (!selectedOrderInstanceId) return;
            const selectedOrder = you?.hand?.find(o => o.instanceId === selectedOrderInstanceId);
            if (selectedOrder?.type !== 'Drone') return;
            playDrone(selectedOrder.id);
          }}
          style={{
            width: '80px',
            height: '60px',
            backgroundColor: '#333',
            border: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone')
              ? '2px solid #ffd400'
              : '2px solid #555',
            borderRadius: '4px',
            opacity: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone') ? 0.85 : 0.3,
            boxShadow: selectedOrderInstanceId && you?.hand?.some(o => o.instanceId === selectedOrderInstanceId && o.type === 'Drone')
              ? '0 0 10px rgba(255,212,0,0.9)'
              : 'none',
            cursor: yourTurn ? 'pointer' : 'default'
          }}
        />
      ))}
    </div>
  );
};

export default MobilePlayerDrones;

